import React, { useState } from 'react';
import { MaterialLine, MaterialRole, ReactionTemplate, BomFieldDefinition } from './types';
import { Trash2, Plus, Edit2, Info } from 'lucide-react';
import { getReactionTemplate } from './chemConfig';
import { StructureEditor } from './StructureEditor';

interface BomTabProps {
  materials: MaterialLine[];
  reactionType: string;
  onUpdate: (materials: MaterialLine[]) => void;
}

const MATERIAL_ROLES: MaterialRole[] = ['Reactant', 'Reagent', 'Solvent', 'Product', 'Intermediate'];

/**
 * BomTab Component (Phase 2: Template-Driven)
 * 
 * Dynamic BOM table that renders columns based on the reaction template.
 * Each reaction type can define custom fields beyond the standard columns.
 * 
 * Features:
 * - Template-based column rendering
 * - Custom metadata fields per reaction type
 * - Role-specific field visibility
 * - Dynamic field types (number, string, select)
 */
export const BomTab: React.FC<BomTabProps> = ({ materials, reactionType, onUpdate }) => {
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingStructureId, setEditingStructureId] = useState<string | null>(null);
  const [detailsMaterialId, setDetailsMaterialId] = useState<string | null>(null);

  // Get reaction template for current reaction type
  const template = getReactionTemplate(reactionType);
  const bomFields = template?.bomFields || [];

  const addMaterial = () => {
    const newMaterial: MaterialLine = {
      id: `mat-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      name: '',
      role: 'Reactant',
      quantity: 0,
      unit: 'g',
      batchNo: '',
      metadata: {}, // For template-specific fields
    };
    onUpdate([...materials, newMaterial]);
    setEditingId(newMaterial.id);
  };

  const deleteMaterial = (id: string) => {
    // if (confirm('Are you sure you want to delete this material?')) {
    onUpdate(materials.filter((m) => m.id !== id));
    // }
  };

  const updateMaterial = (id: string, updates: Partial<MaterialLine>) => {
    onUpdate(
      materials.map((m) => (m.id === id ? { ...m, ...updates } : m))
    );
  };

  const updateMaterialMetadata = (id: string, metaKey: string, value: any) => {
    onUpdate(
      materials.map((m) =>
        m.id === id
          ? {
            ...m,
            metadata: { ...(m.metadata || {}), [metaKey]: value },
          }
          : m
      )
    );
  };

  /**
   * Handle structure update from StructureEditor
   */
  const handleStructureUpdate = (materialId: string, structure: any) => {
    onUpdate(
      materials.map((m) =>
        m.id === materialId
          ? {
            ...m,
            structure,
            metadata: {
              ...(m.metadata || {}),
              molWeight: structure.molWeight, // Sync MW to metadata
            },
          }
          : m
      )
    );
    setEditingStructureId(null);
  };

  /**
   * Check if a field should be visible for a specific material
   */
  const isFieldVisible = (field: BomFieldDefinition, material: MaterialLine): boolean => {
    if (!field.visibleForRoles || field.visibleForRoles.length === 0) {
      return true; // No restriction, always visible
    }
    return field.visibleForRoles.includes(material.role);
  };

  /**
   * Get value for a BOM field from material
   */
  const getFieldValue = (field: BomFieldDefinition, material: MaterialLine): any => {
    // Standard fields
    if (field.id === 'name') return material.name;
    if (field.id === 'role') return material.role;
    if (field.id === 'quantity') return material.quantity;
    if (field.id === 'unit') return material.unit;
    if (field.id === 'batchNo') return material.batchNo || '';

    // Custom fields from metadata
    return material.metadata?.[field.id] ?? '';
  };

  /**
   * Set value for a BOM field on material
   */
  const setFieldValue = (
    field: BomFieldDefinition,
    material: MaterialLine,
    value: any
  ) => {
    // Standard fields
    if (field.id === 'name') {
      updateMaterial(material.id, { name: value });
    } else if (field.id === 'role') {
      updateMaterial(material.id, { role: value as MaterialRole });
    } else if (field.id === 'quantity') {
      updateMaterial(material.id, { quantity: parseFloat(value) || 0 });
    } else if (field.id === 'unit') {
      updateMaterial(material.id, { unit: value });
    } else if (field.id === 'batchNo') {
      updateMaterial(material.id, { batchNo: value });
    } else {
      // Custom metadata field
      updateMaterialMetadata(material.id, field.id, value);
    }
  };

  /**
   * Render input for a field based on its type
   */
  const renderFieldInput = (
    field: BomFieldDefinition,
    material: MaterialLine
  ) => {
    const value = getFieldValue(field, material);
    const visible = isFieldVisible(field, material);

    if (!visible) {
      return <td key={field.id} style={styles.td}><span style={styles.notApplicable}>N/A</span></td>;
    }

    switch (field.type) {
      case 'select':
        return (
          <td key={field.id} style={styles.td}>
            <select
              style={styles.select}
              value={value}
              onChange={(e) => setFieldValue(field, material, e.target.value)}
            >
              {field.options?.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </td>
        );

      case 'number':
        return (
          <td key={field.id} style={styles.td}>
            <input
              type="number"
              style={styles.inputNumber}
              value={value}
              onChange={(e) => setFieldValue(field, material, e.target.value)}
              placeholder={field.placeholder}
              step="0.01"
              min="0"
            />
          </td>
        );

      case 'string':
      default:
        return (
          <td key={field.id} style={styles.td}>
            <input
              type="text"
              style={styles.input}
              value={value}
              onChange={(e) => setFieldValue(field, material, e.target.value)}
              placeholder={field.placeholder}
            />
          </td>
        );
    }
  };

  const calculateTotalMass = () => {
    return materials
      .filter((m) => m.unit === 'g' || m.unit === 'mg' || m.unit === 'kg')
      .reduce((sum, m) => {
        let mass = m.quantity;
        if (m.unit === 'mg') mass = mass / 1000;
        if (m.unit === 'kg') mass = mass * 1000;
        return sum + mass;
      }, 0);
  };

  if (!template) {
    return (
      <div style={styles.container}>
        <div style={styles.errorMessage}>
          No BOM template found for reaction type: {reactionType}
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div>
          <h3 style={styles.heading}>Bill of Materials (BOM)</h3>
          <p style={styles.subheading}>Template: {template.label}</p>
        </div>
        <button style={styles.addButton} onClick={addMaterial}>
          <Plus size={16} />
          <span>Add Material</span>
        </button>
      </div>

      {materials.length === 0 ? (
        <div style={styles.emptyState}>
          <p style={styles.emptyText}>No materials added yet</p>
          <p style={styles.emptySubtext}>Click "Add Material" to get started</p>
        </div>
      ) : (
        <>
          <div style={styles.tableWrapper}>
            <table style={styles.table}>
              <thead>
                <tr>
                  {bomFields.map((field) => (
                    <th key={field.id} style={styles.th}>
                      {field.label}
                      {field.required && <span style={styles.required}> *</span>}
                    </th>
                  ))}
                  <th style={styles.th}>Structure</th>
                  <th style={styles.thActions}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {materials.map((material) => (
                  <tr key={material.id} style={styles.tr}>
                    {bomFields.map((field) => renderFieldInput(field, material))}
                    <td style={styles.td}>
                      {editingStructureId === material.id ? (
                        <div style={styles.structureEditorContainer}>
                          <StructureEditor
                            value={material.structure?.smiles || material.structure?.molfile}
                            format={material.structure?.molfile ? 'mol' : 'smiles'}
                            onChange={(structure) => handleStructureUpdate(material.id, structure)}
                            onClose={() => setEditingStructureId(null)}
                          />
                        </div>
                      ) : (
                        <div style={styles.structureCell}>
                          {material.structure ? (
                            <div style={styles.structureInfo}>
                              <div style={styles.structureBadge}>
                                {material.structure.formula || 'Structure'}
                              </div>
                              {material.structure.molWeight && (
                                <div style={styles.structureMW}>
                                  MW: {material.structure.molWeight.toFixed(1)} g/mol
                                </div>
                              )}
                              <button
                                style={styles.editStructureButton}
                                onClick={() => setEditingStructureId(material.id)}
                                title="Edit structure"
                              >
                                <Edit2 size={14} />
                              </button>
                            </div>
                          ) : (
                            <button
                              style={styles.addStructureButton}
                              onClick={() => setEditingStructureId(material.id)}
                              title="Add structure"
                            >
                              <Plus size={14} />
                              <span>Add</span>
                            </button>
                          )}
                        </div>
                      )}
                    </td>
                    <td style={styles.tdActions}>
                      <div style={styles.actionButtons}>
                        {material.structure && (
                          <button
                            style={styles.detailsButton}
                            onClick={() => setDetailsMaterialId(material.id)}
                            title="View structure details"
                          >
                            <Info size={16} />
                          </button>
                        )}
                        <button
                          style={styles.deleteButton}
                          onClick={() => deleteMaterial(material.id)}
                          title="Delete material"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Summary Stats */}
          <div style={styles.summary}>
            <div style={styles.summaryItem}>
              <span style={styles.summaryLabel}>Total Materials:</span>
              <span style={styles.summaryValue}>{materials.length}</span>
            </div>
            <div style={styles.summaryItem}>
              <span style={styles.summaryLabel}>Total Mass (g equiv):</span>
              <span style={styles.summaryValue}>{calculateTotalMass().toFixed(2)} g</span>
            </div>
            <div style={styles.summaryItem}>
              <span style={styles.summaryLabel}>Reactants:</span>
              <span style={styles.summaryValue}>
                {materials.filter((m) => m.role === 'Reactant').length}
              </span>
            </div>
            <div style={styles.summaryItem}>
              <span style={styles.summaryLabel}>Products:</span>
              <span style={styles.summaryValue}>
                {materials.filter((m) => m.role === 'Product').length}
              </span>
            </div>
          </div>
        </>
      )}

      {/* Structure Details Modal */}
      {detailsMaterialId && (
        <StructureDetailsModal
          material={materials.find((m) => m.id === detailsMaterialId)}
          onClose={() => setDetailsMaterialId(null)}
        />
      )}
    </div>
  );
};

/**
 * Structure Details Modal Component
 */
interface StructureDetailsModalProps {
  material: MaterialLine | undefined;
  onClose: () => void;
}

const StructureDetailsModal: React.FC<StructureDetailsModalProps> = ({ material, onClose }) => {
  if (!material || !material.structure) {
    return null;
  }

  const structure = material.structure;

  return (
    <div style={styles.modalOverlay} onClick={onClose}>
      <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
        <div style={styles.modalHeader}>
          <h3 style={styles.modalTitle}>Structure Details: {material.name || 'Unnamed Material'}</h3>
          <button style={styles.modalCloseButton} onClick={onClose}>
            ×
          </button>
        </div>
        <div style={styles.modalBody}>
          <div style={styles.detailsGrid}>
            <div style={styles.detailItem}>
              <label style={styles.detailLabel}>Molecular Formula:</label>
              <div style={styles.detailValue}>
                {structure.formula || <span style={styles.noData}>Not available</span>}
              </div>
            </div>
            <div style={styles.detailItem}>
              <label style={styles.detailLabel}>Molecular Weight:</label>
              <div style={styles.detailValue}>
                {structure.molWeight !== undefined && structure.molWeight !== null
                  ? `${structure.molWeight.toFixed(2)} g/mol`
                  : <span style={styles.noData}>Not available</span>}
              </div>
            </div>
            <div style={styles.detailItem}>
              <label style={styles.detailLabel}>SMILES:</label>
              <div style={styles.detailValue}>
                {structure.smiles ? (
                  <code style={styles.codeValue}>{structure.smiles}</code>
                ) : (
                  <span style={styles.noData}>Not available</span>
                )}
              </div>
            </div>
            <div style={styles.detailItem}>
              <label style={styles.detailLabel}>InChI Key:</label>
              <div style={styles.detailValue}>
                {structure.inchiKey ? (
                  <code style={styles.codeValue}>{structure.inchiKey}</code>
                ) : (
                  <span style={styles.noData}>Not available</span>
                )}
              </div>
            </div>
            {structure.molfile && (
              <div style={styles.detailItem}>
                <label style={styles.detailLabel}>MOLfile:</label>
                <div style={styles.detailValue}>
                  <pre style={styles.molfileValue}>{structure.molfile}</pre>
                </div>
              </div>
            )}
            {structure.elementCounts && Object.keys(structure.elementCounts).length > 0 && (
              <div style={styles.detailItem}>
                <label style={styles.detailLabel}>Elemental Composition:</label>
                <div style={styles.detailValue}>
                  {Object.entries(structure.elementCounts)
                    .map(([el, count]) => `${el}${count > 1 ? count : ''}`)
                    .join(' ')}
                </div>
              </div>
            )}
          </div>
        </div>
        <div style={styles.modalFooter}>
          <button style={styles.modalButton} onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    padding: '20px',
    height: '100%',
    overflowY: 'auto',
    overflowX: 'hidden', // Prevent horizontal scroll on container, let table wrapper handle it
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '20px',
  },
  heading: {
    margin: '0 0 4px 0',
    fontSize: '18px',
    fontWeight: 600,
    color: '#1e293b',
  },
  subheading: {
    margin: 0,
    fontSize: '13px',
    color: '#64748b',
  },
  addButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '8px 16px',
    fontSize: '14px',
    fontWeight: 500,
    color: '#fff',
    backgroundColor: '#10b981',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
  },
  emptyState: {
    textAlign: 'center',
    padding: '60px 20px',
    backgroundColor: '#f8fafc',
    borderRadius: '8px',
  },
  emptyText: {
    margin: '0 0 8px 0',
    fontSize: '16px',
    fontWeight: 500,
    color: '#64748b',
  },
  emptySubtext: {
    margin: 0,
    fontSize: '14px',
    color: '#94a3b8',
  },
  errorMessage: {
    padding: '16px',
    backgroundColor: '#fee2e2',
    border: '1px solid #fecaca',
    borderRadius: '6px',
    color: '#991b1b',
    fontSize: '14px',
  },
  tableWrapper: {
    overflowX: 'auto',
    overflowY: 'visible',
    marginBottom: '20px',
    width: '100%',
    maxWidth: '100%',
    WebkitOverflowScrolling: 'touch', // Smooth scrolling on iOS
  },
  table: {
    width: 'auto',
    minWidth: '100%',
    borderCollapse: 'collapse',
    fontSize: '13px',
    tableLayout: 'auto', // Allow columns to expand based on content
  },
  th: {
    padding: '10px 12px',
    textAlign: 'left',
    fontWeight: 600,
    color: '#475569',
    backgroundColor: '#f1f5f9',
    borderBottom: '2px solid #cbd5e1',
    whiteSpace: 'nowrap',
    minWidth: '120px', // Minimum column width
  },
  thActions: {
    padding: '10px 8px',
    textAlign: 'center',
    fontWeight: 600,
    color: '#475569',
    backgroundColor: '#f1f5f9',
    borderBottom: '2px solid #cbd5e1',
    width: '80px',
  },
  required: {
    color: '#ef4444',
  },
  tr: {
    borderBottom: '1px solid #e2e8f0',
  },
  td: {
    padding: '8px 12px',
    whiteSpace: 'nowrap',
    minWidth: '120px', // Minimum cell width to prevent squishing
  },
  tdActions: {
    padding: '8px',
    textAlign: 'center',
  },
  input: {
    width: '100%',
    minWidth: '150px', // Increased minimum width
    padding: '6px 8px',
    fontSize: '13px',
    border: '1px solid #cbd5e1',
    borderRadius: '4px',
    outline: 'none',
    boxSizing: 'border-box',
  },
  inputNumber: {
    width: '100%',
    minWidth: '100px', // Increased minimum width
    padding: '6px 8px',
    fontSize: '13px',
    border: '1px solid #cbd5e1',
    borderRadius: '4px',
    outline: 'none',
    boxSizing: 'border-box',
  },
  select: {
    width: '100%',
    minWidth: '120px', // Increased minimum width
    padding: '6px 8px',
    fontSize: '13px',
    border: '1px solid #cbd5e1',
    borderRadius: '4px',
    backgroundColor: '#fff',
    cursor: 'pointer',
    outline: 'none',
  },
  notApplicable: {
    color: '#94a3b8',
    fontStyle: 'italic',
    fontSize: '12px',
  },
  deleteButton: {
    padding: '6px',
    color: '#ef4444',
    backgroundColor: 'transparent',
    border: '1px solid #fecaca',
    borderRadius: '4px',
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
  },
  summary: {
    display: 'flex',
    gap: '20px',
    padding: '16px',
    backgroundColor: '#f8fafc',
    borderRadius: '8px',
    flexWrap: 'wrap',
  },
  summaryItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  summaryLabel: {
    fontSize: '12px',
    color: '#64748b',
    fontWeight: 500,
  },
  summaryValue: {
    fontSize: '18px',
    color: '#1e293b',
    fontWeight: 600,
  },
  structureCell: {
    padding: '4px 12px',
    minWidth: '180px', // Increased width for structure column
  },
  structureInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  structureBadge: {
    fontSize: '11px',
    fontWeight: 500,
    color: '#2563eb',
    backgroundColor: '#eff6ff',
    padding: '2px 6px',
    borderRadius: '4px',
    display: 'inline-block',
  },
  structureMW: {
    fontSize: '11px',
    color: '#64748b',
  },
  editStructureButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    padding: '4px 8px',
    fontSize: '11px',
    color: '#2563eb',
    backgroundColor: 'transparent',
    border: '1px solid #bfdbfe',
    borderRadius: '4px',
    cursor: 'pointer',
  },
  addStructureButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    padding: '4px 8px',
    fontSize: '11px',
    color: '#64748b',
    backgroundColor: '#f1f5f9',
    border: '1px dashed #cbd5e1',
    borderRadius: '4px',
    cursor: 'pointer',
  },
  structureEditorContainer: {
    padding: '8px',
    backgroundColor: '#f8fafc',
    border: '1px solid #e2e8f0',
    borderRadius: '6px',
    maxWidth: '300px',
  },
  actionButtons: {
    display: 'flex',
    gap: '6px',
    alignItems: 'center',
    justifyContent: 'center',
  },
  detailsButton: {
    padding: '6px',
    color: '#2563eb',
    backgroundColor: 'transparent',
    border: '1px solid #bfdbfe',
    borderRadius: '4px',
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.2s',
  },
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  },
  modalContent: {
    backgroundColor: '#fff',
    borderRadius: '8px',
    maxWidth: '600px',
    width: '90%',
    maxHeight: '80vh',
    display: 'flex',
    flexDirection: 'column',
    boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2)',
  },
  modalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '20px',
    borderBottom: '1px solid #e2e8f0',
  },
  modalTitle: {
    margin: 0,
    fontSize: '18px',
    fontWeight: 600,
    color: '#1e293b',
  },
  modalCloseButton: {
    background: 'none',
    border: 'none',
    fontSize: '24px',
    color: '#64748b',
    cursor: 'pointer',
    padding: '0',
    width: '32px',
    height: '32px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '4px',
    transition: 'all 0.2s',
  },
  modalBody: {
    padding: '20px',
    overflowY: 'auto',
    flex: 1,
  },
  modalFooter: {
    padding: '16px 20px',
    borderTop: '1px solid #e2e8f0',
    display: 'flex',
    justifyContent: 'flex-end',
  },
  modalButton: {
    padding: '8px 16px',
    fontSize: '14px',
    fontWeight: 500,
    color: '#fff',
    backgroundColor: '#2563eb',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
  },
  detailsGrid: {
    display: 'grid',
    gap: '16px',
  },
  detailItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
  },
  detailLabel: {
    fontSize: '13px',
    fontWeight: 600,
    color: '#64748b',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  detailValue: {
    fontSize: '14px',
    color: '#1e293b',
    wordBreak: 'break-word',
  },
  codeValue: {
    fontFamily: 'monospace',
    fontSize: '13px',
    backgroundColor: '#f1f5f9',
    padding: '4px 8px',
    borderRadius: '4px',
    display: 'inline-block',
  },
  molfileValue: {
    fontFamily: 'monospace',
    fontSize: '11px',
    backgroundColor: '#f1f5f9',
    padding: '12px',
    borderRadius: '4px',
    overflow: 'auto',
    maxHeight: '200px',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-all',
  },
  noData: {
    color: '#94a3b8',
    fontStyle: 'italic',
  },
};
